import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  searchGooglePlaces,
  updateStoreGooglePlace,
  type GooglePlaceResult,
} from "@/api/adminService";

type Props = {
  storeId: string;
  currentPlace?: {
    placeId: string;
    name: string;
    address: string;
    rating: number;
    reviewCount: number;
  };
  onDone: () => void; // call after successful update so parent can refetch
};

const GooglePlaceEditor: React.FC<Props> = ({ storeId, currentPlace, onDone }) => {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<GooglePlaceResult[]>([]);
  const [selected, setSelected] = useState<GooglePlaceResult | null>(null);
  const [removeOld, setRemoveOld] = useState(true);
  const [importNew, setImportNew] = useState(true);
  const [applying, setApplying] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setResults([]);
    try {
      const res = await searchGooglePlaces({ q: query.trim() });
      if (res.ok && res.data) setResults(res.data);
      else toast.error("خطأ", { description: res.error || "فشل البحث" });
    } finally {
      setSearching(false);
    }
  };

  const handleAssign = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      const res = await updateStoreGooglePlace({
        storeId,
        placeId: selected.placeId,
        removeOldReviews: removeOld,
        importNewReviews: importNew,
      });
      if (res.ok) {
        toast.success("تم التحديث", { description: res.message || `تم ربط المكان: ${selected.name}` });
        onDone();
      } else {
        toast.error("خطأ", { description: res.error || "فشل تحديث المكان" });
      }
    } finally {
      setApplying(false);
    }
  };

  const handleRemove = async () => {
    setApplying(true);
    try {
      const res = await updateStoreGooglePlace({
        storeId,
        placeId: undefined,
        removeOldReviews: removeOld,
        importNewReviews: false,
      });
      if (res.ok) {
        toast.success("تم الحذف", { description: res.message || "تم إزالة مكان Google" });
        onDone();
      } else {
        toast.error("خطأ", { description: res.error || "فشل إزالة المكان" });
      }
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Current assignment */}
      {currentPlace ? (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <div className="font-semibold text-blue-900">{currentPlace.name}</div>
          <div className="text-blue-700 text-xs mt-0.5">{currentPlace.address}</div>
          <div className="text-blue-500 text-xs mt-0.5">
            ⭐ {currentPlace.rating} · {currentPlace.reviewCount} تقييم · ID: {currentPlace.placeId}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">لا يوجد مكان Google مرتبط بهذا المتجر.</p>
      )}

      {/* Search */}
      <div className="flex gap-2">
        <Input
          placeholder="ابحث باسم المتجر..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          className="flex-1 text-sm"
          disabled={applying}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={handleSearch}
          disabled={searching || !query.trim() || applying}
        >
          {searching ? "..." : "بحث"}
        </Button>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
          {results.map((place) => (
            <button
              key={place.placeId}
              onClick={() => setSelected(selected?.placeId === place.placeId ? null : place)}
              className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 transition-colors ${
                selected?.placeId === place.placeId
                  ? "bg-blue-50 border-blue-300"
                  : "hover:bg-gray-50"
              }`}
            >
              <div className="font-medium">{place.name}</div>
              <div className="text-gray-500 text-xs">{place.address}</div>
              <div className="text-gray-400 text-xs">⭐ {place.rating} · {place.reviewCount} تقييم</div>
            </button>
          ))}
        </div>
      )}

      {/* Options */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={removeOld}
            onChange={(e) => setRemoveOld(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          حذف تقييمات Google Maps الحالية
        </label>
        {selected && (
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={importNew}
              onChange={(e) => setImportNew(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            استيراد تقييمات من المكان الجديد
          </label>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap">
        {selected && (
          <Button size="sm" onClick={handleAssign} disabled={applying}>
            {applying ? "جاري التطبيق..." : `ربط "${selected.name}"`}
          </Button>
        )}
        {currentPlace && (
          <Button size="sm" variant="destructive" onClick={handleRemove} disabled={applying}>
            {applying ? "جاري الحذف..." : "إزالة المكان"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default GooglePlaceEditor;
